// S1 product-list filter helpers (Module 01 §4). Pure + exported so the toolbar
// state and the attribute-predicate parsing are unit-testable without React.
//
// Attribute predicates mirror the API contract (§5 `GET /products`):
// `attr.<key>=<value>` where the value is an exact match or carries a `gte:` /
// `lte:` prefix for range queries (e.g. `cross_section_mm2=gte:50`). The
// foundation query builder (`buildProductsQuery`) turns the returned map into the
// repeatable `attr.<key>` query params — this module only parses the human input.

/** Product lifecycle statuses (§2.4) used by the status filter. */
export const PRODUCT_STATUSES = ["draft", "active", "discontinued"] as const;
export type ProductStatus = (typeof PRODUCT_STATUSES)[number];

/** Make-or-buy classifications (§2.4) used by the make/buy filter. */
export const MAKE_OR_BUY = ["make", "buy", "both"] as const;
export type MakeOrBuy = (typeof MAKE_OR_BUY)[number];

/**
 * Attribute-predicate comparison operators the API understands (§5
 * `GET /products`): an exact match, or a `gte:` / `lte:` prefixed range bound.
 */
export const ATTR_OPS = ["eq", "gte", "lte"] as const;
export type AttrOp = (typeof ATTR_OPS)[number];

export interface AttrPredicate {
  /** The attribute KEY as the schema declares it (`cross_section_mm2`). */
  key: string;
  op: AttrOp;
  value: string;
}

/**
 * Build the `{key: value}` map the query builder turns into `attr.<key>` params.
 *
 * THIS REPLACED A FREE-TEXT DSL. S1 used to take a raw `key=value` string with
 * `gte:`/`lte:` prefixes, requiring the user to know the exact `snake_case`
 * database key — the very string the attribute chips and the schema form are
 * supposed to keep off the screen. Typing the human-plausible
 * `Cross section=50` sent `attr.Cross section=50`, the API answered 400, and
 * the table replaced itself with a system error card whose Retry could never
 * succeed. The key is now CHOSEN from the schema, so an unsendable predicate
 * cannot be expressed, and the operator is a control rather than a prefix.
 *
 * An incomplete predicate (no key, or no value) narrows nothing and is dropped.
 */
export function buildAttrPredicates(predicate: AttrPredicate): Record<string, string> {
  const key = predicate.key.trim();
  const value = predicate.value.trim();
  if (key.length === 0 || value.length === 0) return {};
  return { [key]: predicate.op === "eq" ? value : `${predicate.op}:${value}` };
}

/** True when at least one filter narrows the list (drives the no-match state). */
export function hasActiveFilters(state: {
  q?: string | undefined;
  kind?: string | undefined;
  status?: string | undefined;
  schemaCode?: string | undefined;
  makeOrBuy?: string | undefined;
  attributes: Record<string, string>;
}): boolean {
  return (
    (state.q !== undefined && state.q.trim().length > 0) ||
    (state.kind !== undefined && state.kind.length > 0) ||
    (state.status !== undefined && state.status.length > 0) ||
    (state.schemaCode !== undefined && state.schemaCode.length > 0) ||
    (state.makeOrBuy !== undefined && state.makeOrBuy.length > 0) ||
    Object.keys(state.attributes).length > 0
  );
}
